import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import axios from "axios";

import { APP_API_V1_BASE } from "../../../config/api";
const API_URL = `${APP_API_V1_BASE}/organizations`;
const TEXT_PRIMARY = "#111827";
const TEXT_MUTED = "#667085";

const TYPE_OPTIONS = [
  { value: "OEM", label: "OEM" },
  { value: "DISTRIBUTOR", label: "Distributor" },
  { value: "CUSTOMER", label: "Customer" },
  { value: "ENTERPRISE", label: "Enterprise" },
];

export default function AddOrganizationModel({
  open,
  onClose,
  onSave,
  mode = "create",
  initialData = null,
}) {
  const [errors, setErrors] = useState({});
  const validate = () => {
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = "Required";
    }

    if (!form.code.trim()) {
      newErrors.code = "Required";
    }

    if (!form.type) {
      newErrors.type = "Required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const [form, setForm] = useState({
    name: "",
    code: "",
    type: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initialData) {
      setForm({
        name: initialData.name || "",
        code: initialData.code || "",
        type: String(initialData.type || "").toUpperCase(),
      });
    } else {
      setForm({
        name: "",
        code: "",
        type: "",
      });
    }
    setError("");
  }, [open, mode, initialData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "code" ? value.toUpperCase() : value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    // if (!form.name.trim() || !form.code.trim() || !form.type) {
    //   setError("Name, code, and organization type are required.");
    //   return;
    // }

    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("authToken");
      const payload = {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        type: form.type,
      };

      const res =
        mode === "create"
          ? await axios.post(API_URL, payload, {
              headers: { Authorization: `Bearer ${token}` },
            })
          : await axios.patch(`${API_URL}/${initialData.id}`, payload, {
              headers: { Authorization: `Bearer ${token}` },
            });

      onSave({
        ...(mode === "edit" ? initialData : {}),
        ...payload,
        ...(res?.data || {}),
      });
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          `Failed to ${mode === "edit" ? "update" : "create"} organization`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          flexWrap: "wrap",
          // border: "1px solid rgba(17, 24, 39, 0.08)",
          boxShadow: "0 28px 64px rgba(15,23,42,0.16)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            p: { xs: 2.5, md: 3 },
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
            borderBottom: "1px solid rgba(255,255,255,0.15)",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between", // 👈 pushes content & icon apart
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            {/* LEFT CONTENT */}
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "-0.03em",
                  color: "#fff",
                }}
              >
                {mode === "edit" ? "Update Organization" : "Add Organization"}
              </Typography>

              <Typography variant="body2" sx={{ mt: 0.5, color: "#b4b0b0ff" }}>
                {mode === "edit"
                  ? "Refine tenant profile details and keep the directory consistent."
                  : "Create a new tenant record with a clean organization profile."}
              </Typography>
            </Box>

            {/* RIGHT ICON */}
            <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2.5, md: 3 }, backgroundColor: "#fcfcfd" }}>
        <Stack spacing={2.25} sx={{ mt: 2.5 }}>
          <TextField
            label="Organization Name"
            name="name"
            value={form.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            required
            autoFocus
            placeholder="Enter organization display name"
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Organization Code"
              name="code"
              value={form.code}
              onChange={handleChange}
              error={!!errors.code}
              helperText={errors.code}
              fullWidth
              required
              placeholder="Short unique code"
              disabled={mode === "edit"}
              helperText={
                mode === "edit"
                  ? "Organization code is locked after creation."
                  : "Use a short operational identifier, for example CORE or CDN."
              }
            />

            <TextField
              label="Organization Type"
              name="type"
              select
              value={form.type}
              onChange={handleChange}
              error={!!errors.type}
              helperText={errors.type}
              fullWidth
              required
            >
              {TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ borderRadius: 2.5 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, md: 3 },
          pb: 3,
          pt: 0.5,
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
          sx={{ textTransform: "none", fontWeight: 700, color: TEXT_MUTED , color:"#1976d2"}}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SaveRoundedIcon />
            )
          }
          endIcon={!loading ? <ArrowOutwardRoundedIcon /> : null}
          sx={{
            borderRadius: 3,
            px: 2.25,
            py: 1.1,
            textTransform: "none",
            fontWeight: 700,
            background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
            boxShadow: "0 12px 28px rgba(15,23,42,0.18)",
            "&:hover": { bgcolor: "#1f2937" },
          }}
        >
          {loading
            ? "Saving..."
            : mode === "edit"
              ? "Update Organization"
              : "Create Organization"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
